// src/Components/NaturalQuery/NaturalQueryInterface.tsx
import React, { useState, useRef, useEffect } from 'react';
import { Row, Col, Card } from 'react-bootstrap';
import { FaRobot, FaPaperPlane, FaLightbulb, FaChartLine, FaBoxes, FaSpinner, FaMicrophone, FaMicrophoneSlash, FaDollarSign } from 'react-icons/fa';
import api from '../../Services/api';
import { useAppSelector } from '../../redux/store';
import { notifyError } from '../notify/notify-component';
import { QueryResponse, Message } from './interfaces';
import { formatDate, suggestedQueries } from './utils';
import { 
  renderSellers, 
  renderProducts, 
  renderMonthlyData, 
  renderPaymentMethods, 
  renderExpensesIncome,
  renderEmptyState
} from './renderers';

const NaturalQueryInterface: React.FC = () => {
  const storeMe = useAppSelector(({ storeMeReducer }) => storeMeReducer.value);
  const [query, setQuery] = useState('');
  const [messages, setMessages] = useState<Message[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [isListening, setIsListening] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  useEffect(() => {
    scrollToBottom();
  }, [messages]);

  const startVoiceRecognition = () => {
    if ('webkitSpeechRecognition' in window) {
      const recognition = new (window as any).webkitSpeechRecognition();
      recognition.continuous = false;
      recognition.interimResults = false;
      recognition.lang = 'pt-BR';

      recognition.onstart = () => {
        setIsListening(true);
      };

      recognition.onresult = (event: any) => {
        const transcript = event.results[0][0].transcript;
        setQuery(transcript);
        setIsListening(false);
      };

      recognition.onerror = () => {
        setIsListening(false);
        notifyError('Erro no reconhecimento de voz');
      };

      recognition.onend = () => {
        setIsListening(false);
      };

      recognition.start();
    } else {
      notifyError('Reconhecimento de voz não suportado neste navegador');
    }
  };

  // Função principal para renderizar dados
  const renderResponseData = (response: QueryResponse) => {
    const components = [];

    // Verificar vendedores
    if (response.sellers !== undefined) {
      if (response.sellers.length > 0) {
        components.push(<div key="sellers">{renderSellers(response.sellers)}</div>);
      } else {
        components.push(<div key="sellers-empty">{renderEmptyState('sellers', response.period)}</div>);
      }
    }

    // Verificar produtos
    if (response.products !== undefined) {
      if (response.products.length > 0) {
        components.push(<div key="products">{renderProducts(response.products)}</div>);
      } else {
        components.push(<div key="products-empty">{renderEmptyState('products', response.period)}</div>);
      }
    }

    // Verificar dados mensais
    if (response.data !== undefined) {
      if (response.data.length > 0) {
        components.push(<div key="monthly">{renderMonthlyData(response.data)}</div>);
      } else {
        components.push(<div key="monthly-empty">{renderEmptyState('monthly', response.period)}</div>);
      }
    }

    // Verificar lojas/métodos de pagamento
    if (response.stores !== undefined) {
      if (response.stores.length > 0) {
        components.push(<div key="stores">{renderPaymentMethods(response.stores)}</div>);
      } else {
        components.push(<div key="stores-empty">{renderEmptyState('payments', response.period)}</div>);
      }
    }

    // Verificar receitas e despesas
    if (response.receitas !== undefined || response.despesas !== undefined) {
      const hasReceitas = response.receitas && response.receitas.length > 0;
      const hasDespesas = response.despesas && response.despesas.length > 0;
      
      if (hasReceitas || hasDespesas) {
        components.push(<div key="financial">{renderExpensesIncome(response.receitas, response.despesas)}</div>);
      } else {
        components.push(<div key="financial-empty">{renderEmptyState('financial', response.period)}</div>);
      }
    }

    // Se nenhum componente foi adicionado mas há período, mostrar estado vazio geral
    if (components.length === 0 && response.period) {
      components.push(<div key="general-empty">{renderEmptyState('general', response.period)}</div>);
    }

    return components.length > 0 ? components : null;
  };

  const handleSubmit = async (queryText?: string) => {
    const searchQuery = queryText || query.trim();
    
    if (!searchQuery) {
      notifyError('Por favor, digite uma pergunta');
      return;
    }

    if (!storeMe?.storeActive?.id) {
      notifyError('Loja não identificada');
      return;
    }

    const userMessage: Message = {
      id: Date.now().toString(),
      type: 'user',
      content: searchQuery,
      timestamp: new Date()
    };

    const loadingMessage: Message = {
      id: (Date.now() + 1).toString(),
      type: 'loading',
      content: 'Analisando sua pergunta...',
      timestamp: new Date()
    };

    setMessages(prev => [...prev, userMessage, loadingMessage]);
    setQuery('');
    setIsLoading(true);

    try {
      const response = await api.post('/api/natural-query', {
        query: searchQuery,
        store_id: storeMe.storeActive.id
      });

      const result = response.data;
      console.log('Resposta da API:', result);

      setMessages(prev => prev.filter(msg => msg.type !== 'loading'));

      const assistantMessage: Message = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: result.message || 'Aqui estão os resultados da sua consulta:',
        response: result,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);

    } catch (error: any) {
      console.log('Erro na consulta:', error);
      setMessages(prev => prev.filter(msg => msg.type !== 'loading'));

      const errorMessage = error.response?.data?.message || 'Não consegui processar sua pergunta. Tente reformular ou seja mais específico.';
      
      const assistantMessage: Message = {
        id: (Date.now() + 2).toString(),
        type: 'assistant',
        content: errorMessage,
        timestamp: new Date()
      };

      setMessages(prev => [...prev, assistantMessage]);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSubmit();
    }
  };

  return (
    <div style={{ 
      height: '100vh', 
      display: 'flex', 
      flexDirection: 'column', 
      margin: '0',
      padding: '0',
      overflow: 'hidden'
    }}>
      {/* Header */}
      <div style={{
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        color: 'white',
        padding: '15px 20px',
        textAlign: 'center',
        flexShrink: 0
      }}>
        <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '15px' }}>
          <FaRobot size={28} />
          <div>
            <h2 style={{ margin: '0', fontSize: '20px', fontWeight: '600' }}>
              Assistente de Consultas IA
            </h2>
            <p style={{ margin: '5px 0 0', opacity: 0.9, fontSize: '13px' }}>
              Faça perguntas sobre seus dados em linguagem natural
            </p>
          </div>
        </div>
      </div>

      {/* Chat Container */}
      <div style={{
        flex: 1,
        backgroundColor: '#f8f9fa',
        display: 'flex',
        flexDirection: 'column',
        overflow: 'hidden'
      }}>
        {/* Messages Area */}
        <div style={{
          flex: 1,
          padding: '15px',
          overflowY: 'auto',
          backgroundColor: 'white',
          minHeight: 0
        }}>
          {messages.length === 0 ? (
            <div style={{ textAlign: 'center', padding: '20px 15px' }}>
              <FaLightbulb size={40} style={{ color: '#ffc107', marginBottom: '15px' }} />
              <h3 style={{ color: '#6c757d', marginBottom: '15px', fontSize: '18px' }}>Como posso ajudar você hoje?</h3>
              <p style={{ color: '#6c757d', marginBottom: '20px', fontSize: '14px' }}>
                Faça perguntas sobre vendas, estoque, funcionários ou qualquer dado do seu negócio
              </p>
              
              <Row style={{ marginBottom: '20px' }}>
                <Col md={4} style={{ marginBottom: '10px' }}>
                  <Card style={{ border: 'none', backgroundColor: '#f8f9fa', height: '100%' }}>
                    <Card.Body style={{ padding: '12px' }}>
                      <FaChartLine style={{ color: '#28a745', fontSize: '20px', marginBottom: '8px' }} />
                      <h6 style={{ fontSize: '14px', marginBottom: '5px' }}>Vendas:</h6>
                      <small style={{ color: '#6c757d', fontSize: '12px' }}>
                        "Vendas do mês passado"<br/>
                        "Melhor funcionário em vendas"
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4} style={{ marginBottom: '10px' }}>
                  <Card style={{ border: 'none', backgroundColor: '#f8f9fa', height: '100%' }}>
                    <Card.Body style={{ padding: '12px' }}>
                      <FaBoxes style={{ color: '#007bff', fontSize: '20px', marginBottom: '8px' }} />
                      <h6 style={{ fontSize: '14px', marginBottom: '5px' }}>Produtos:</h6>
                      <small style={{ color: '#6c757d', fontSize: '12px' }}>
                        "Top 10 produtos mais vendidos"<br/>
                        "Produtos com baixo estoque"
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
                <Col md={4}>
                  <Card style={{ border: 'none', backgroundColor: '#f8f9fa', height: '100%' }}>
                    <Card.Body style={{ padding: '12px' }}>
                      <FaDollarSign style={{ color: '#dc3545', fontSize: '20px', marginBottom: '8px' }} />
                      <h6 style={{ fontSize: '14px', marginBottom: '5px' }}>Financeiro:</h6>
                      <small style={{ color: '#6c757d', fontSize: '12px' }}>
                        "Despesas do mês"<br/>
                        "Faturamento por pagamento"
                      </small>
                    </Card.Body>
                  </Card>
                </Col>
              </Row>

              <div>
                <p style={{ fontSize: '13px', color: '#6c757d', marginBottom: '12px' }}>
                  Ou experimente uma destas perguntas:
                </p>
                <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', justifyContent: 'center' }}>
                  {suggestedQueries.slice(0, 3).map((suggestion, index) => (
                    <button
                      key={index}
                      onClick={() => handleSubmit(suggestion)}
                      style={{
                        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '15px',
                        padding: '6px 12px',
                        fontSize: '11px',
                        cursor: 'pointer',
                        transition: 'all 0.2s',
                        maxWidth: '200px'
                      }}
                      onMouseOver={(e) => e.currentTarget.style.transform = 'scale(1.05)'}
                      onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
                    >
                      {suggestion}
                    </button>
                  ))}
                </div>
              </div>
            </div>
          ) : (
            messages.map((message) => (
              <div key={message.id} style={{
                marginBottom: '15px',
                display: 'flex',
                justifyContent: message.type === 'user' ? 'flex-end' : 'flex-start'
              }}>
                <div style={{
                  maxWidth: '95%',
                  padding: '12px 16px',
                  borderRadius: '15px',
                  background: message.type === 'user' 
                    ? 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)'
                    : message.type === 'loading' 
                    ? '#fff3cd' 
                    : '#f8f9fa',
                  color: message.type === 'user' ? 'white' : '#495057',
                  border: message.type !== 'user' ? '1px solid #dee2e6' : 'none',
                  fontSize: '14px'
                }}>
                  {message.type === 'loading' ? (
                    <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
                      <FaSpinner style={{ animation: 'spin 1s linear infinite' }} />
                      {message.content}
                    </div>
                  ) : (
                    <>
                      <div>{message.content}</div>
                      {message.response && renderResponseData(message.response)}
                    </>
                  )}
                  <div style={{
                    fontSize: '10px',
                    opacity: 0.7,
                    marginTop: '4px',
                    textAlign: message.type === 'user' ? 'right' : 'left'
                  }}>
                    {message.timestamp.toLocaleTimeString()}
                  </div>
                </div>
              </div>
            ))
          )}
          <div ref={messagesEndRef} />
        </div>

        {/* Input Area */}
        <div style={{
          padding: '15px',
          backgroundColor: '#f8f9fa',
          borderTop: '1px solid #dee2e6',
          flexShrink: 0
        }}>
          <div style={{
            display: 'flex',
            gap: '8px',
            alignItems: 'center'
          }}>
            <div style={{ position: 'relative', flex: 1 }}>
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onKeyPress={handleKeyPress}
                placeholder="Digite sua pergunta aqui... (Ex: Quais funcionários mais venderam este mês?)"
                disabled={isLoading}
                style={{
                  width: '100%',
                  padding: '12px 80px 12px 16px',
                  borderRadius: '20px',
                  border: '2px solid #dee2e6',
                  fontSize: '13px',
                  outline: 'none',
                  transition: 'border-color 0.2s'
                }}
                onFocus={(e) => e.target.style.borderColor = '#667eea'}
                onBlur={(e) => e.target.style.borderColor = '#dee2e6'}
              />
              
              <button
                onClick={startVoiceRecognition}
                disabled={isLoading}
                style={{
                  position: 'absolute',
                  right: '45px',
                  top: '50%',
                  transform: 'translateY(-50%)',
                  background: 'none',
                  border: 'none',
                  color: isListening ? '#dc3545' : '#6c757d',
                  fontSize: '16px',
                  cursor: 'pointer',
                  padding: '4px'
                }}
                title={isListening ? "Gravando..." : "Usar voz"}
              >
                {isListening ? <FaMicrophoneSlash /> : <FaMicrophone />}
              </button>
            </div>
            
            <button
              onClick={() => handleSubmit()}
              disabled={isLoading || !query.trim()}
              style={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                color: 'white',
                border: 'none',
                borderRadius: '50%',
                width: '44px',
                height: '44px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                cursor: 'pointer',
                opacity: isLoading || !query.trim() ? 0.6 : 1,
                transition: 'all 0.2s'
              }}
              onMouseOver={(e) => !isLoading && query.trim() && (e.currentTarget.style.transform = 'scale(1.05)')}
              onMouseOut={(e) => e.currentTarget.style.transform = 'scale(1)'}
            >
              {isLoading ? <FaSpinner style={{ animation: 'spin 1s linear infinite' }} /> : <FaPaperPlane />}
            </button>
          </div>
          
          <div style={{ marginTop: '8px', fontSize: '10px', color: '#6c757d', textAlign: 'center' }}>
            Loja: <strong>{storeMe?.storeActive?.name || 'Não identificada'}</strong> | 
            Pressione Enter para enviar ou use o ícone do microfone para falar
          </div>
        </div>
      </div>

      <style>
        {`
          @keyframes spin {
            0% { transform: rotate(0deg); }
            100% { transform: rotate(360deg); }
          }
        `}
      </style>
    </div>
  );
};

export default NaturalQueryInterface;